import { LineItemMode } from '@danone-global/ct/interfaces'
import { CartIcon } from '@danone-global/internal/react/components/icons'
import Modal from '@danone-global/internal/react/components/modal'
import QuickView from '@danone-global/internal/react/components/quick-view'
import {
  ButtonVariants,
  IconButton,
  Loader,
  Typography,
} from '@danone-global/internal/react/components/ui'
import {
  analytics,
  analyticsCheckoutBtntEvent,
  DTC_AUTH_STATUS,
  Theme,
  useCart,
  useCore,
} from '@danone-global/internal/react/core'
import { makeStyles } from '@material-ui/styles'
import * as Sentry from '@sentry/react'
import clsx from 'clsx'
import { COOKIE_DATALAYER_CHECKOUT_BUTTON } from '@danone-global/internal/graphql'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Cookies from 'universal-cookie'

export interface Props {
  url: string

  variant?: 'default' | 'badge'

  buttonVariant?: ButtonVariants

  analytics?: boolean
}

export const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      position: 'relative',
    },

    button: {
      display: 'flex',
      alignItems: 'center',
    },

    itemCount: {
      marginLeft: theme.spacing(),
    },

    default: {
      padding: theme.spacing(1, 2),
    },

    badge: {
      '& $itemCount': {
        position: 'absolute',
        height: theme.utils.getSize(18),
        lineHeight: theme.utils.getSize(11, { forceUnit: true }),
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
        fontSize: theme.utils.getSize(10),
        borderRadius: '1em',
        padding: theme.spacing(0.2, 0.5),
        border: theme.utils.createStyle(
          theme.utils.getSize(2, { forceUnit: true }),
          'solid',
          theme.palette.common.white,
        ),
        top: `-${theme.utils.getSize(1, { forceUnit: true })}`,
        right: 0,
      },
    },
    modalRoot: {
      display: 'flex',
      alignItems: 'center',
    },
    modalLoader: {
      marginRight: theme.spacing(),
    },
    modalMessage: {
      marginTop: 0,
      marginBottom: 0,
    },
  }),
  { name: 'ShoppingCart' },
)

export const ShoppingCart: React.FC<Props> = ({
  url,
  variant = 'default',
  buttonVariant = 'brand',
  analytics: withAnalytics = true,
}) => {
  const [cart] = useCart()
  const classes = useStyles()
  const cookies = new Cookies()
  const [authenticationModalOpen, setAuthenticationModalOpen] = useState(false)
  const [authenticationModalType, setAuthenticationModalType] = useState('')

  const handleOnClick = React.useCallback(() => {
    window.location.assign(url)
  }, [url])

  const totalQuantity =
    cart?.lineItems
      .filter((l) => l.lineItemMode === LineItemMode.Standard)
      ?.reduce((count, lineItem) => (count += lineItem.quantity), 0) ?? 0

  const authLogin = () => {
    sessionStorage.removeItem(DTC_AUTH_STATUS)
    setAuthenticationModalType('login')
    setAuthenticationModalOpen(true)
  }

  const authLogout = () => {
    sessionStorage.removeItem(DTC_AUTH_STATUS)
    setAuthenticationModalType('logout')
    setAuthenticationModalOpen(true)
  }

  const authStop = () => {
    sessionStorage.removeItem(DTC_AUTH_STATUS)
    setAuthenticationModalOpen(false)
  }

  const authStatusSession = sessionStorage.getItem(DTC_AUTH_STATUS)
  const { config, localeConfig } = useCore()
  const intl = useIntl()

  useEffect(() => {
    if(cookies.get(COOKIE_DATALAYER_CHECKOUT_BUTTON) === 'true') {
      cookies.remove(COOKIE_DATALAYER_CHECKOUT_BUTTON, { path: '/' })
      const checkout_btn_text=intl.formatMessage({
        id: 'widgets.shopping-cart-container.cart-container.continue-checkout',
        defaultMessage: 'Proceed to checkout',
      })
      ;(async () => {
        analytics.push(await analyticsCheckoutBtntEvent(cart, config, localeConfig, checkout_btn_text))
      })()
    } else {
      analytics.pop()
    }
    if (authStatusSession === 'login') {
      authLogin()
    } else if (authStatusSession === 'logout') {
      authLogout()
    }

    window.addEventListener('dtc-authentication-login', authLogin)
    window.addEventListener('dtc-authentication-logout', authLogout)
    window.addEventListener('dtc-authentication-stop', authStop)

    return () => {
      window.removeEventListener('dtc-authentication-login', authLogin)
      window.removeEventListener('dtc-authentication-logout', authLogout)
      window.removeEventListener('dtc-authentication-stop', authStop)
    }
  }, [authStatusSession])

  return (
    <div className={classes.root} data-testid="widget-shopping-cart">
      <Modal open={authenticationModalOpen}>
        <div className={classes.modalRoot}>
          <Loader
            className={classes.modalLoader}
            color="dark"
            variant="small"
          />

          <Typography className={classes.modalMessage}>
            {authenticationModalType === 'login' ? (
              <FormattedMessage
                defaultMessage="We'll log you in - one moment please"
                id="authentication.login"
              />
            ) : (
              <FormattedMessage
                defaultMessage="We'll log you out - one moment please"
                id="authentication.logout"
              />
            )}
          </Typography>
        </div>
      </Modal>

      <QuickView goToCartPage={handleOnClick} withAnalytics={withAnalytics} />

      <IconButton
        className={clsx(classes.root, classes[variant])}
        color={
          variant === 'default' && buttonVariant !== 'flat'
            ? 'white'
            : 'primary-main'
        }
        data-testid="btn.shopping-cart"
        onClick={handleOnClick}
        variant={variant === 'default' ? buttonVariant : 'flat'}
      >
        <span className={classes.button}>
          <CartIcon height={18} width={18} />

          <Typography
            className={classes.itemCount}
            component="span"
            data-testid="widget-shipping-cart-quantity"
            fontWeight={variant === 'default' ? 'default' : 'bold'}
            variant="inherit"
          >
            {totalQuantity}
          </Typography>
        </span>
      </IconButton>
    </div>
  )
}

export default Sentry.withProfiler(ShoppingCart, {
  name: 'WidgetShoppingCart',
})
